import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";
import { api, errMsg, dt, inr, loadRazorpay } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, StatusBadge } from "@/components/Brand";
import { inputCls } from "@/pages/Auth";

const DocField = ({ label, type, value, onChange, expiry }) => {
  const [busy, setBusy] = useState(false);
  const up = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("doc_type", type);
    fd.append("file", file);
    try { const { data } = await api.post("/driver/documents", fd); onChange({ ...value, doc_id: data.doc_id, filename: data.filename }); } catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border p-3" data-testid={`doc-field-${type}`}>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <label className="flex cursor-pointer items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold hover:bg-slate-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {value?.doc_id ? "Replace" : "Upload"}
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={up} data-testid={`doc-upload-${type}`} />
        </label>
        <span className="truncate text-xs text-slate-500">{value?.filename || (value?.doc_id ? "Uploaded" : "No file")}</span>
        {expiry && <input type="date" className="ml-auto rounded-lg border px-2 py-1.5 text-xs" value={value?.expiry || ""} onChange={(e) => onChange({ ...value, expiry: e.target.value })} aria-label={`${label} expiry`} data-testid={`doc-expiry-${type}`} />}
      </div>
    </div>
  );
};

const ProfileForm = ({ d, editable, reload }) => {
  const [f, setF] = useState({ name: d.name, phone: d.phone, address: d.address, licence_number: d.licence_number, emergency_contact_name: d.emergency_contact_name || "", emergency_contact_phone: d.emergency_contact_phone || "", documents: d.documents || {} });
  const doc = (k) => (v) => setF({ ...f, documents: { ...f.documents, [k]: v } });
  const save = async (e) => {
    e.preventDefault();
    try { await api.put("/driver/profile", f); toast.success("Profile saved"); reload(); } catch (x) { toast.error(errMsg(x)); }
  };
  const s = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={save} className="space-y-4" data-testid="driver-profile-form">
      <fieldset disabled={!editable} className="grid gap-3 sm:grid-cols-2">
        <input className={inputCls} placeholder="Full name" value={f.name} onChange={s("name")} required data-testid="drv-name-input" />
        <input className={inputCls} placeholder="Mobile" value={f.phone} onChange={s("phone")} required data-testid="drv-phone-input" />
        <input className={`${inputCls} sm:col-span-2`} placeholder="Address" value={f.address} onChange={s("address")} required data-testid="drv-address-input" />
        <input className={inputCls} placeholder="Driving licence number" value={f.licence_number} onChange={s("licence_number")} required data-testid="drv-licence-input" />
        <input className={inputCls} placeholder="Emergency contact name" value={f.emergency_contact_name} onChange={s("emergency_contact_name")} data-testid="drv-emg-name-input" />
        <input className={inputCls} placeholder="Emergency contact phone" value={f.emergency_contact_phone} onChange={s("emergency_contact_phone")} data-testid="drv-emg-phone-input" />
        <DocField label="Driving licence" type="driving_licence" value={f.documents.driving_licence} onChange={doc("driving_licence")} expiry />
        <DocField label="Address proof" type="address_proof" value={f.documents.address_proof} onChange={doc("address_proof")} />
        <DocField label="Police verification certificate" type="pvc" value={f.documents.pvc} onChange={doc("pvc")} expiry />
        <DocField label="Profile photo (optional)" type="photo" value={f.documents.photo} onChange={doc("photo")} />
      </fieldset>
      {editable && <button className="rounded-full bg-[#0A192F] px-6 py-2.5 text-sm font-semibold text-white" data-testid="drv-profile-save-btn">Save profile</button>}
    </form>
  );
};

const emptyV = { registration: "", make: "", model: "", category: "Sedan", rate_class: "Sedan", seats: 4, luggage: 2, ownership: "self", owner_name: "", documents: {} };
const VehicleForm = ({ initial, onDone }) => {
  const [v, setV] = useState(initial || emptyV);
  const classes = { Sedan: ["Sedan"], SUV: ["Ertiga", "Crysta", "Hycross"], Hatchback: ["Hatchback"] };
  const s = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const doc = (k) => (val) => setV({ ...v, documents: { ...v.documents, [k]: val } });
  const save = async (e) => {
    e.preventDefault();
    const body = { ...v, seats: Number(v.seats), luggage: v.luggage === "" ? null : Number(v.luggage) };
    try {
      if (initial?.vehicle_id) await api.put(`/driver/vehicles/${initial.vehicle_id}`, body); else await api.post("/driver/vehicles", body);
      toast.success("Vehicle saved"); onDone();
    } catch (x) { toast.error(errMsg(x)); }
  };
  return (
    <form onSubmit={save} className="grid gap-3 rounded-2xl border bg-slate-50 p-5 sm:grid-cols-3" data-testid="vehicle-form">
      <input className={inputCls} placeholder="Registration (KA01AB1234)" value={v.registration} onChange={s("registration")} required data-testid="veh-reg-input" />
      <input className={inputCls} placeholder="Make (e.g. Maruti)" value={v.make} onChange={s("make")} required data-testid="veh-make-input" />
      <input className={inputCls} placeholder="Model (e.g. Dzire)" value={v.model} onChange={s("model")} required data-testid="veh-model-input" />
      <select className={inputCls} value={v.category} onChange={(e) => setV({ ...v, category: e.target.value, rate_class: classes[e.target.value][0] })} data-testid="veh-category-select">
        {Object.keys(classes).map((c) => <option key={c}>{c}</option>)}
      </select>
      <select className={inputCls} value={v.rate_class} onChange={s("rate_class")} data-testid="veh-class-select">{classes[v.category].map((c) => <option key={c}>{c}</option>)}</select>
      <div className="grid grid-cols-2 gap-2">
        <input type="number" className={inputCls} min="2" max="12" value={v.seats} onChange={s("seats")} aria-label="Passenger seats" data-testid="veh-seats-input" />
        <input type="number" className={inputCls} min="0" max="10" value={v.luggage ?? ""} onChange={s("luggage")} aria-label="Luggage bags" data-testid="veh-luggage-input" />
      </div>
      <select className={inputCls} value={v.ownership} onChange={s("ownership")} data-testid="veh-ownership-select"><option value="self">I own this vehicle</option><option value="other">Owned by someone else</option></select>
      {v.ownership === "other" && <input className={inputCls} placeholder="Owner name" value={v.owner_name} onChange={s("owner_name")} data-testid="veh-owner-input" />}
      <div className="grid gap-2 sm:col-span-3 sm:grid-cols-2">
        <DocField label="Registration certificate" type="rc" value={v.documents.rc} onChange={doc("rc")} />
        <DocField label="Insurance" type="insurance" value={v.documents.insurance} onChange={doc("insurance")} expiry />
        <DocField label="Permit" type="permit" value={v.documents.permit} onChange={doc("permit")} expiry />
        {v.ownership === "other" && <DocField label="Owner consent letter" type="owner_consent" value={v.documents.owner_consent} onChange={doc("owner_consent")} />}
      </div>
      <button className="rounded-full bg-[#0A192F] px-6 py-2.5 text-sm font-semibold text-white sm:col-span-3 sm:justify-self-start" data-testid="veh-save-btn">Save vehicle</button>
    </form>
  );
};

const Vehicles = ({ data, reload }) => {
  const [adding, setAdding] = useState(false);
  const [edit, setEdit] = useState(null);
  const approved = data.driver.driver_approval_status === "approved";
  const appEditable = ["draft", "corrections_requested"].includes(data.driver.application.status);
  const act = async (fn) => { try { await fn(); reload(); } catch (x) { toast.error(errMsg(x)); } };
  return (
    <div className="space-y-4" data-testid="driver-vehicles">
      {data.vehicles.map((v) => (
        <div key={v.vehicle_id} className="rounded-xl border bg-white p-4" data-testid={`vehicle-card-${v.registration}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-bold text-[#0A192F]">{v.registration} · {v.make} {v.model} <span className="text-sm font-normal text-slate-500">({v.category}/{v.rate_class}, {v.seats} seats)</span></p>
            <div className="flex items-center gap-2"><StatusBadge value={v.eligibility_status} />{v.supplementary && <StatusBadge value="supplementary" />}</div>
          </div>
          {v.review_note && <p className="mt-1 text-sm text-amber-800">Reviewer: {v.review_note}</p>}
          <div className="mt-2 flex gap-3 text-sm">
            {["draft", "corrections_requested"].includes(v.eligibility_status) && <button onClick={() => setEdit(v)} className="font-semibold text-[#0A192F]" data-testid={`veh-edit-${v.registration}`}>Edit</button>}
            {v.eligibility_status === "draft" && <button onClick={() => act(() => api.delete(`/driver/vehicles/${v.vehicle_id}`))} className="text-red-700" data-testid={`veh-delete-${v.registration}`}>Remove</button>}
            {v.supplementary && ["draft", "corrections_requested"].includes(v.eligibility_status) && <button onClick={() => act(() => api.post(`/driver/vehicles/${v.vehicle_id}/submit`))} className="font-semibold text-emerald-700" data-testid={`veh-submit-${v.registration}`}>Submit for review</button>}
          </div>
          {edit?.vehicle_id === v.vehicle_id && <div className="mt-3"><VehicleForm initial={edit} onDone={() => { setEdit(null); reload(); }} /></div>}
        </div>
      ))}
      {(appEditable || approved) && (adding ? <VehicleForm onDone={() => { setAdding(false); reload(); }} /> :
        <button onClick={() => setAdding(true)} className="rounded-full border border-dashed border-slate-400 px-5 py-2.5 text-sm font-semibold" data-testid="add-vehicle-btn">+ Add {approved ? "another vehicle (supplementary review)" : "vehicle"}</button>)}
    </div>
  );
};

const Subscription = ({ data, reload }) => {
  const t = data.subscription_terms;
  const [consent, setConsent] = useState(false);
  const d = data.driver;
  const start = async () => {
    try {
      const { data: r } = await api.post("/driver/subscription/start", { consent, consent_text_version: "2026-10-v1" });
      if (!(await loadRazorpay())) return toast.error("Could not load Razorpay checkout");
      new window.Razorpay({
        key: r.key_id, subscription_id: r.subscription_id, name: "NAMMAAFLEET", description: "Partner subscription",
        handler: async (resp) => { try { const { data: v } = await api.post("/driver/subscription/verify", resp); toast.success(v.active ? "Registration active!" : `Pending: ${v.blockers.join(", ")}`); reload(); } catch (x) { toast.error(errMsg(x)); } },
      }).open();
    } catch (x) { toast.error(errMsg(x)); }
  };
  return (
    <div className="max-w-2xl space-y-5" data-testid="subscription-panel">
      <div className="rounded-2xl border bg-white p-6 text-sm leading-relaxed">
        <p className="text-lg font-bold text-[#0A192F]">Partner subscription</p>
        {!t.terms_approved && <p className="mt-2 rounded-lg bg-amber-50 p-3 text-amber-900" data-testid="terms-pending-note">Proposed terms — subject to final business, provider and legal approval.</p>}
        <ul className="mt-3 list-disc space-y-1 pl-5">
          <li>Amount: <b>{inr(t.amount_paise)}</b> per month, billed monthly by auto-debit</li>
          <li>Duration: {t.months} months · Scheduled total {inr(t.amount_paise * t.months)} before applicable taxes or fees</li>
          <li>{t.taxes_note}</li>
          <li>The mandate is set up through Razorpay. You can cancel future debits at any time; see the <a href="/refund-policy" className="underline">Cancellation & Refund Policy</a>.</li>
          <li>Failed debits are retried by the provider; if the subscription is halted, your registration is suspended until payment is restored.</li>
        </ul>
      </div>
      <div className="flex flex-wrap gap-2 text-sm">Subscription <StatusBadge value={d.subscription_status} /> Payment <StatusBadge value={d.payment_status} /> Mandate <StatusBadge value={d.mandate_status} /></div>
      {d.activation_blockers?.length > 0 && <p className="text-sm text-slate-600" data-testid="activation-blockers">Activation pending: {d.activation_blockers.join(" · ")}</p>}
      {d.driver_approval_status !== "approved" ? <p className="text-sm text-slate-500">Subscription opens after your application is approved.</p> : d.registration_status !== "active" && (
        <>
          <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" data-testid="subscription-consent-checkbox" /> I agree to a recurring monthly debit of {inr(t.amount_paise)} for {t.months} months and the partner subscription terms above.</label>
          <button disabled={!consent} onClick={start} className="rounded-full bg-[#FFB703] px-6 py-3 text-sm font-bold text-[#0A192F] disabled:opacity-40" data-testid="subscription-start-btn">Set up subscription</button>
        </>
      )}
    </div>
  );
};

const NEXT = { Assigned: "Confirmed", Confirmed: "Driver En Route", "Driver En Route": "Trip Started", "Trip Started": "Completed" };
const Trips = () => {
  const [rows, setRows] = useState(null);
  const [done, setDone] = useState({});
  const load = useCallback(() => { api.get("/driver/trips").then((r) => setRows(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  const go = async (b) => {
    const next = NEXT[b.status];
    const extra = next === "Completed" ? { actual_km: done.km ? Number(done.km) : null, actual_minutes: done.min ? Number(done.min) : null, toll_paise: Math.round((Number(done.toll) || 0) * 100), parking_paise: Math.round((Number(done.parking) || 0) * 100) } : {};
    try { await api.post(`/driver/trips/${b.booking_id}/status`, { status: next, ...extra }); toast.success(`Marked ${next}`); load(); } catch (x) { toast.error(errMsg(x)); }
  };
  if (!rows) return <p className="text-slate-500">Loading…</p>;
  if (!rows.length) return <p className="rounded-xl border border-dashed p-10 text-center text-slate-500" data-testid="trips-empty">No trips assigned yet.</p>;
  return (
    <div className="space-y-3">
      {rows.map((b) => (
        <div key={b.booking_id} className="rounded-xl border bg-white p-5 text-sm" data-testid={`trip-card-${b.booking_id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2"><p className="font-bold text-[#0A192F]">{b.booking_id} · {b.service_label}</p><StatusBadge value={b.status} /></div>
          <p className="mt-1">{dt(b.pickup_at_utc)} · {b.pickup.address} → {b.drop?.address || "Local package"}</p>
          <p>Customer: {b.customer.name} · +91 {b.customer.phone} · Vehicle {b.assignment?.registration}</p>
          {b.flight?.number && <p>Flight: {b.flight.number}</p>}
          {b.status === "Trip Started" && (
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[["km", "Total km"], ["min", "Total minutes"], ["toll", "Toll ₹"], ["parking", "Parking ₹"]].map(([k, l]) => <input key={k} className={inputCls} placeholder={l} value={done[k] || ""} onChange={(e) => setDone({ ...done, [k]: e.target.value })} data-testid={`trip-${k}-input`} />)}
            </div>
          )}
          {NEXT[b.status] && <button onClick={() => go(b)} className="mt-3 rounded-full bg-[#0A192F] px-4 py-2 font-semibold text-white" data-testid={`trip-next-${b.booking_id}`}>Mark {NEXT[b.status]}</button>}
        </div>
      ))}
    </div>
  );
};

export default function DriverPortal() {
  const { logout } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("Application");
  const load = useCallback(() => { api.get("/driver/me").then((r) => setData(r.data)).catch((x) => toast.error(errMsg(x))); }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="p-16 text-slate-500">Loading partner portal…</div>;
  const d = data.driver;
  const editable = ["draft", "corrections_requested"].includes(d.application.status);
  const submit = async () => { try { await api.post("/driver/application/submit"); toast.success("Application submitted"); load(); } catch (x) { toast.error(errMsg(x)); } };
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="nf-navy"><div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5"><Logo light /><div className="flex items-center gap-4"><span className="text-sm text-slate-300">Partner Portal</span><button onClick={logout} className="text-sm text-[#FFB703]" data-testid="driver-logout-btn">Logout</button></div></div></header>
      <main className="mx-auto max-w-6xl px-5 py-10">
        <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="driver-status-bar">
          <span>Application <StatusBadge value={d.application.status} testid="driver-application-status" /></span>
          <span>Approval <StatusBadge value={d.driver_approval_status} /></span>
          <span>Registration <StatusBadge value={d.registration_status} testid="driver-registration-status" /></span>
          <span>Subscription <StatusBadge value={d.subscription_status} /></span>
        </div>
        {d.application.corrections?.length > 0 && (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="corrections-list">
            <p className="font-semibold">Corrections requested</p>{d.application.corrections.map((c, i) => <p key={i}>• {c.field}: {c.note}</p>)}
          </div>
        )}
        <div className="mt-6 flex gap-1 overflow-x-auto border-b">
          {["Application", "Vehicles", "Subscription", "Trips"].map((t) => <button key={t} onClick={() => setTab(t)} data-testid={`driver-tab-${t.toLowerCase()}`} className={`px-4 py-2.5 text-sm font-semibold ${tab === t ? "border-b-2 border-[#FFB703] text-[#0A192F]" : "text-slate-500"}`}>{t}</button>)}
        </div>
        <div className="mt-8">
          {tab === "Application" && (
            <div className="space-y-6">
              <ProfileForm d={d} editable={editable} reload={load} />
              {editable && (
                <div className="rounded-2xl border bg-white p-5">
                  <p className="font-semibold text-[#0A192F]">Ready to submit?</p>
                  <p className="text-sm text-slate-500">Your profile and all {data.vehicles.filter((v) => ["draft", "corrections_requested"].includes(v.eligibility_status)).length} vehicle(s) are reviewed together.</p>
                  {data.issues.length > 0 && <ul className="mt-2 text-sm text-red-700" data-testid="application-issues">{data.issues.map((i, k) => <li key={k}>• {i.scope} — {i.field}: {i.issue}</li>)}</ul>}
                  <button onClick={submit} className="mt-3 rounded-full bg-[#FFB703] px-6 py-2.5 text-sm font-bold text-[#0A192F]" data-testid="submit-application-btn">Submit application</button>
                </div>
              )}
            </div>
          )}
          {tab === "Vehicles" && <Vehicles data={data} reload={load} />}
          {tab === "Subscription" && <Subscription data={data} reload={load} />}
          {tab === "Trips" && <Trips />}
        </div>
      </main>
    </div>
  );
}
